import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useRegistrationIntakeService,
  useCareRequestService,
} from '@actschurch/ccms-sdk';
import type {
  AnySourceConfig, FilterValues, Row, ServerSort, SourceKey,
} from '../types';
import { endOfDay, toSDKDate, toSDKDateTime } from '@/utils/dateTime';
import { isEmpty } from '../utils';
import { useStreamSource } from './useStreamSource';

interface Args {
  sourceKey: SourceKey;
  source: AnySourceConfig;
  applied: FilterValues;
  sort: ServerSort | null;
  search: string;
  page: number;
  pageSize: number;
}

export interface StreamInfo {
  loading: boolean;
  progress: number;
  loadedCount: number;
}

interface Result {
  rows: Row[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  /** Fetches one arbitrary page — used by the export loop (paged sources only). */
  fetchPage: (skip: number, take: number) => Promise<Row[]>;
  /** Streamed sources only — drives <StreamProgress />. */
  stream: StreamInfo;
}

const IDLE_STREAM: StreamInfo = { loading: false, progress: 0, loadedCount: 0 };

const toRow = (item: object): Row => Object.assign({}, item) as Row;
type ListViewResponse = { items?: object[]; totalCount?: number } | undefined;

export function useSourceData({
  sourceKey, source, applied, sort, search, page, pageSize,
}: Args): Result {
  const intakeService = useRegistrationIntakeService();
  const requestService = useCareRequestService();

  const [rows, setRows] = useState<Row[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestSeq = useRef(0);

  // Streamed sources (registration, case). Always called to keep hook order stable.
  const streamed = useStreamSource({ sourceKey, source, search });

  const buildFilterModel = useMemo(() => {
    return (): Record<string, unknown> => {
      const model: Record<string, unknown> = {
        ...(sort ? { sortBy: sort.sortBy, sortDir: sort.sortDir } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      };
      // ⚠️ Status ids are strings and Pending is "0" — "0" is truthy in JS, but never convert it
      // to a number before this check or Pending would silently mean "everything".
      if (sourceKey === 'intake') {
        if (!isEmpty(applied.status)) model.intakeStatusID = String(applied.status);
        if (!isEmpty(applied.course)) model.courseID = String(applied.course);
        if (!isEmpty(applied.location)) model.locationID = String(applied.location);
        if (applied.submittedFrom instanceof Date) {
          model.submittedFrom = Number(toSDKDate(applied.submittedFrom));
        }
        if (applied.submittedTo instanceof Date) {
          model.submittedTo = Number(toSDKDateTime(endOfDay(applied.submittedTo)));
        }
      } else if (sourceKey === 'request') {
        if (!isEmpty(applied.status)) model.requestStatusID = String(applied.status);
        if (!isEmpty(applied.need)) model.needID = String(applied.need);
        if (!isEmpty(applied.location)) model.locationID = String(applied.location);
        if (applied.submittedFrom instanceof Date) {
          model.submittedFrom = Number(toSDKDate(applied.submittedFrom));
        }
        if (applied.submittedTo instanceof Date) {
          model.submittedTo = Number(toSDKDateTime(endOfDay(applied.submittedTo)));
        }
      }
      return model;
    };
  }, [sourceKey, applied, sort, search]);

  /** Paged sources only (intake, request). */
  const pagedFetch = useMemo(() => {
    if (source.mode !== 'paged') return null;

    if (sourceKey === 'intake') {
      return async (model: Record<string, unknown>) => {
        const res = (await intakeService.listView(model as never)) as ListViewResponse;
        return { items: (res?.items ?? []).map(toRow), totalCount: res?.totalCount ?? 0 };
      };
    }
    if (sourceKey === 'request') {
      return async (model: Record<string, unknown>) => {
        const res = (await requestService.listView(model as never)) as ListViewResponse;
        return { items: (res?.items ?? []).map(toRow), totalCount: res?.totalCount ?? 0 };
      };
    }
    return null;
  }, [source.mode, sourceKey, intakeService, requestService]);

  const fetchPage = useMemo(() => {
    if (!pagedFetch) {
      return async (_skip: number, _take: number): Promise<Row[]> => [];
    }
    return async (skip: number, take: number): Promise<Row[]> => {
      const res = await pagedFetch({ ...buildFilterModel(), skip, take });
      return res.items;
    };
  }, [pagedFetch, buildFilterModel]);

  useEffect(() => {
    if (!pagedFetch) return; // streamed sources are handled by useStreamSource

    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await pagedFetch({
          ...buildFilterModel(),
          skip: (page - 1) * pageSize,
          take: pageSize,
        });
        if (seq !== requestSeq.current) return; // a newer request already answered
        setRows(res.items);
        setTotalCount(res.totalCount);
      } catch (e) {
        if (seq !== requestSeq.current) return;
        console.error(`[${sourceKey}] failed to load rows:`, e);
        setRows([]);
        setTotalCount(0);
        setError('Could not load records.');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    })();
  }, [pagedFetch, sourceKey, buildFilterModel, page, pageSize]);

  if (source.mode === 'streamed') {
    return {
      rows: streamed.rows,
      totalCount: streamed.rows.length,
      loading: streamed.loading,
      error: streamed.error,
      fetchPage,
      stream: {
        loading: streamed.loading,
        progress: streamed.progress,
        loadedCount: streamed.loadedCount,
      },
    };
  }

  return { rows, totalCount, loading, error, fetchPage, stream: IDLE_STREAM };
}